import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { duracion } from '@/modules/tablero/duracion'
import { useRecorridos } from '@/modules/tablero/useRecorrido'
/**
 * Cuánto puede estar una orden en una estación antes de que preocupe.
 *
 * NO SON LOS UMBRALES DEL TABLERO. Aquel mide cuánto lleva una orden sin
 * precio —donde dos días ya es mucho—; este mide cuánto lleva parada en su
 * estación, y una obra puede estar en ejecución una semana sin que nada vaya
 * mal.
 *
 * Con los umbrales del tablero (24 y 72 horas) TODAS las tarjetas de MEGA
 * salían con filete rojo. Un tablero donde todo está marcado no marca nada:
 * el ojo deja de buscar el color y se pierde justo lo que había que ver.
 */
const HORAS_DEMORADA = 72
const HORAS_VIEJA = 168
import { iniciales, pesos } from '@/shared/utilidades/formato'

import { CalendarioDeTrabajo } from './CalendarioDeTrabajo'
import { COLUMNAS, repartirEnColumnas } from './recorrido'
import type { OrdenServicioConRelaciones } from './tipos'
import estilos from './TableroRecorrido.module.css'

/**
 * A QUIÉN SE ESTÁ ESPERANDO, QUE NO SIEMPRE SOMOS NOSOTROS.
 *
 * Cotización y Aprobación son una sola columna, así que la espera se cuenta en
 * la tarjeta. Y no son la misma espera: la de World Office no se puede apurar;
 * la de gerencia sí, y es la que hay que perseguir; la del cliente se persigue
 * por teléfono, no por dentro; y una rechazada ya no espera a nadie —hay que
 * volver a cotizar o cerrarla—.
 */
function aQuienSeEspera(o: OrdenServicioConRelaciones): string {
  const estado = o.cotizacion?.estado

  if (estado === 'rechazada') return 'El cliente la rechazó'
  if (estado === 'enviada') return 'Con el cliente'
  if (estado === 'cotizada') return 'Esperando su decisión'
  return 'Esperando a World Office'
}

/** El color de esa espera, en el rótulo. */
function tonoDeEspera(o: OrdenServicioConRelaciones): string {
  const estado = o.cotizacion?.estado

  if (estado === 'rechazada') return estilos.esperaRechazada
  if (estado === 'enviada') return estilos.esperaCliente
  if (estado === 'cotizada') return estilos.esperaNuestra
  return ''
}

/**
 * Y EL COLOR DE LA TARJETA ENTERA, que es lo que se ve de lejos.
 *
 * Solo las dos que se salen de lo corriente. Lo normal en esta columna es
 * estar esperando la decisión de gerencia —la mayoría de las tarjetas— y
 * pintarlas todas sería no pintar ninguna: se buscan justamente las que NO
 * están en el caso de siempre.
 *
 * El filete de la izquierda no se toca: ese es la edad y nada más.
 */
function fondoDeLaTarjeta(o: OrdenServicioConRelaciones): string {
  const estado = o.cotizacion?.estado

  if (estado === 'rechazada') return estilos.tarjetaRechazada
  if (estado === 'enviada') return estilos.tarjetaConElCliente
  return ''
}

type PropsTablero = {
  ordenes: OrdenServicioConRelaciones[]
}

/**
 * El recorrido completo en un tablero.
 *
 * UNA SOLA SUPERFICIE, NO NUEVE CAJAS. El trabajo va de izquierda a derecha
 * por estaciones de un mismo proceso; nueve rectángulos flotando con su borde
 * y su sombra lo cuentan como nueve cosas sueltas. Con un fondo compartido y
 * filetes de un píxel se lee como lo que es: un recorrido.
 *
 * CADA COLUMNA DA UNA LECTURA, no solo un rótulo. Debajo del nombre va una
 * barra con la parte del trabajo que carga —el tablero entero se vuelve un
 * histograma que se lee sin abrir una sola tarjeta— y una cifra: en las
 * compuertas, cuánto lleva esperando la más vieja, porque ahí el tiempo
 * detenido cuesta plata; en las demás, cuánto valor hay parado.
 *
 * EL TIEMPO ES LA VARIABLE DEL TABLERO. Lo que hay que ver de un golpe no es
 * cuántas hay, es cuáles llevan mucho. Por eso el filete de la izquierda de
 * cada tarjeta es SU EDAD y nada más —a las veinticuatro horas se pone ámbar,
 * a las setenta y dos rojo, los mismos umbrales del tablero principal—, y la
 * marca de compuerta se fue entera a la columna. Un canal, una variable.
 */

/** «jue, 2 oct, 7:00 a. m.»: cuándo arranca una orden programada. */
function cuandoArranca(iso: string | null): string {
  if (!iso) return 'sin fecha'
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

/** El filete de la tarjeta según lo que lleve parada. */
function tonoDeEdad(horas: number | null | undefined): string {
  if (horas == null) return ''
  if (horas >= HORAS_VIEJA) return estilos.vieja
  if (horas >= HORAS_DEMORADA) return estilos.demorada
  return ''
}

export function TableroRecorrido({ ordenes }: PropsTablero) {
  const navegar = useNavigate()
  const reparto = useMemo(() => repartirEnColumnas(ordenes), [ordenes])

  // Cuánto lleva cada orden en la estación donde está. Sale de la bitácora,
  // no de actualizado_en: cualquier edición mueve esa fecha y el reloj se
  // reiniciaría sin que el trabajo hubiera avanzado.
  const { recorridos, medidoEn } = useRecorridos()

  /*
   * EL RELOJ CORRE SOLO.
   *
   * Las horas vienen de la base al pedirlas; entre una consulta y la otra se
   * les suma lo que va pasando, con un tic por minuto. Así «6,3 h aquí» no se
   * queda congelado mientras la matriz está abierta en la oficina.
   *
   * Y nunca en negativo: una estación cuya marca quedó unos segundos por
   * delante del reloj —o una orden sembrada a mano— decía «-4 min aquí».
   */
  const [ahora, setAhora] = useState(() => Date.now())
  useEffect(() => {
    const tic = setInterval(() => setAhora(Date.now()), 60 * 1000)
    return () => clearInterval(tic)
  }, [])
  const horasAqui = (ordenId: string): number | null => {
    const horas = recorridos.get(ordenId)?.horas_en_estacion
    if (horas == null) return null
    const corridas = medidoEn ? Math.max(0, ahora - medidoEn) / 3_600_000 : 0
    return Math.max(0, Number(horas) + corridas)
  }

  // La columna más cargada manda la escala de las barras. Comparar contra el
  // total daría barras diminutas en cuanto haya nueve estaciones ocupadas.
  const mayor = Math.max(...COLUMNAS.map((c) => (reparto[c.clave] ?? []).length), 1)

  // El calendario de todos los técnicos se abre desde Ejecución, donde están
  // las programadas. En «Por programar» no: ahí no hay nada con fecha, y el
  // botón le cortaba el nombre a la columna.
  const [conCalendario, setConCalendario] = useState(false)
  const abreCalendario = (clave: string) => clave === 'ejecucion'

  return (
    <>
      {conCalendario && <CalendarioDeTrabajo onCerrar={() => setConCalendario(false)} />}
      <div className={`${estilos.marco} desplazable`}>
        <div className={estilos.tablero}>
          {COLUMNAS.map((columna) => {
            const enColumna = reparto[columna.clave] ?? []

            const valor = enColumna.reduce(
              (suma, orden) => suma + (orden.cotizacion?.valor_total ?? 0),
              0
            )

            const masVieja = enColumna.reduce<number | null>((peor, orden) => {
              const horas = horasAqui(orden.id)
              return horas != null && (peor === null || horas > peor) ? horas : peor
            }, null)

            return (
              <section
                key={columna.clave}
                className={`${estilos.columna} ${columna.compuerta ? estilos.compuerta : ''}`}
                aria-label={columna.nombre}
              >
                <header className={estilos.cabecera}>
                  <div className={estilos.tituloColumna}>
                    <span className={estilos.nombre}>{columna.nombre}</span>
                    {abreCalendario(columna.clave) && (
                      <button
                        type="button"
                        className={`${estilos.botonCalendario} ${
                          conCalendario ? estilos.botonCalendarioAbierto : ''
                        }`}
                        onClick={() => setConCalendario((v) => !v)}
                        aria-expanded={conCalendario}
                      >
                        Calendario {conCalendario ? '▴' : '▾'}
                      </button>
                    )}
                    <span
                      className={`${estilos.cuantas} ${enColumna.length === 0 ? estilos.cero : ''}`}
                    >
                      {enColumna.length}
                    </span>
                  </div>

                  {/* Solo cuando hay carga. Una pista vacía al lado de
                    «Despejada» se lee como un filete de separación y ensucia
                    la cabecera sin decir nada que la cuenta no haya dicho. */}
                  {enColumna.length > 0 && (
                    <span className={estilos.carga}>
                      <span
                        className={estilos.cargaLlena}
                        style={{
                          width: `${(enColumna.length / mayor) * 100}%`,
                        }}
                      />
                    </span>
                  )}

                  {/* En una compuerta lo que importa es cuánto lleva esperando la
                    peor; en las demás, cuánto valor hay detenido. */}
                  <span className={estilos.lectura}>
                    {enColumna.length === 0 ? (
                      <span className={estilos.despejada}>Despejada</span>
                    ) : columna.compuerta ? (
                      <>
                        La más vieja{' '}
                        <b className={estilos.lecturaCompuerta}>{duracion(masVieja)}</b>
                      </>
                    ) : valor > 0 ? (
                      <b className={estilos.lecturaValor}>{pesos(valor)}</b>
                    ) : (
                      <span className={estilos.sinPrecio}>Sin precio todavía</span>
                    )}
                  </span>
                </header>

                <div className={`${estilos.cuerpo} desplazable`}>
                  {enColumna.length === 0 ? (
                    <p className={estilos.vacia} aria-hidden="true" />
                  ) : (
                    enColumna.map((orden) => {
                      const horas = horasAqui(orden.id)
                      const quien = orden.orden_trabajo?.colaborador?.nombre ?? null

                      return (
                        <button
                          key={orden.id}
                          type="button"
                          className={`${estilos.tarjeta} ${tonoDeEdad(horas)} ${
                            columna.clave === 'cotizacion' ? fondoDeLaTarjeta(orden) : ''
                          }`}
                          onClick={() => navegar(`/ordenes/${orden.id}`)}
                        >
                          <div className={estilos.encabezado}>
                            <span className={estilos.numero}>
                              {orden.orden_trabajo?.numero ?? orden.numero}
                            </span>

                            {/* Un borrador todavía no tiene número: se dice que
                              está a medias para no confundirlo con uno hecho. */}
                            {orden.cotizacion?.estado === 'borrador' ? (
                              <span className={estilos.borrador}>Borrador</span>
                            ) : (
                              orden.cotizacion?.numero_cotizacion && (
                                <span className={estilos.numeroCotizacion}>
                                  {orden.cotizacion.numero_cotizacion}
                                </span>
                              )
                            )}
                          </div>

                          {/* Cotización y Aprobación son una sola columna, así
                            que la tarjeta dice a quién se está esperando. La
                            de World Office no se puede apurar; la de una
                            persona de MEGA sí, y es la que hay que
                            perseguir. */}
                          {columna.clave === 'cotizacion' && (
                            <p className={`${estilos.espera} ${tonoDeEspera(orden)}`}>
                              {aQuienSeEspera(orden)}
                            </p>
                          )}

                          {/* En Ejecución hay dos cosas distintas: lo que ya
                              arrancó y lo que solo tiene fecha. Se dice cuál. */}
                          {columna.clave === 'ejecucion' && (
                            <p
                              className={`${estilos.espera} ${
                                orden.orden_trabajo?.estado === 'en_ejecucion'
                                  ? estilos.esperaEnSitio
                                  : ''
                              }`}
                            >
                              {orden.orden_trabajo?.estado === 'en_ejecucion'
                                ? 'En la obra'
                                : `Programada · ${cuandoArranca(
                                    orden.orden_trabajo?.fecha_inicio_programada ?? null,
                                  )}`}
                            </p>
                          )}
                          {columna.clave === 'programacion' && (
                            <p className={estilos.espera}>Falta técnico y fecha</p>
                          )}

                          <p className={estilos.cliente}>
                            {orden.cliente?.nombre ?? 'Sin cliente'}
                          </p>

                          {orden.descripcion && (
                            <p className={estilos.descripcion}>{orden.descripcion}</p>
                          )}

                          <div className={estilos.meta}>
                            <span className={estilos.servicio}>
                              {orden.servicio?.codigo && (
                                <span className={estilos.codigoServicio}>
                                  {orden.servicio.codigo}
                                </span>
                              )}
                              {orden.servicio?.nombre ?? '—'}
                            </span>

                            {/* Quién la tiene. De la programación en adelante es
                              la primera pregunta que se hace sobre una orden. */}
                            {quien && (
                              <span className={estilos.quien} title={quien}>
                                <span className={estilos.avatar} aria-hidden="true">
                                  {iniciales(quien)}
                                </span>
                                {quien}
                              </span>
                            )}
                          </div>

                          <div className={estilos.pie}>
                            <span className={`${estilos.edad} ${tonoDeEdad(horas)}`}>
                              {horas != null ? `${duracion(horas)} aquí` : ''}
                            </span>

                            {orden.cotizacion?.valor_total != null && (
                              <span className={estilos.valor}>
                                {pesos(orden.cotizacion.valor_total)}
                              </span>
                            )}
                          </div>

                          {/* En qué va el acta. Estación 6 no es columna: el acta
                            sale al aprobar el precierre y lo que falta después
                            es la firma del cliente, que pasa en paralelo a que
                            el operario devuelva la herramienta. */}
                          {columna.clave === 'cierre' && (
                            <p
                              className={`${estilos.acta} ${
                                orden.orden_trabajo?.acta_enviada_en ? estilos.actaEnviada : ''
                              }`}
                            >
                              {orden.orden_trabajo?.acta_enviada_en
                                ? 'Esperando la firma del cliente'
                                : 'El acta no ha salido'}
                            </p>
                          )}
                        </button>
                      )
                    })
                  )}
                </div>
              </section>
            )
          })}
        </div>
      </div>
    </>
  )
}
